import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';

export interface OutletItem {
  id: string;
  name: string;
  address?: string | null;
  city?: string | null;
  countryCode: string;
}

interface OutletPickerProps {
  selected: string[];
  onChange: (ids: string[]) => void;
}

export function OutletPicker({ selected, onChange }: OutletPickerProps) {
  const { t } = useTranslation();
  const [outlets, setOutlets] = useState<OutletItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get<OutletItem[]>('/claims/outlets')
      .then(setOutlets)
      .catch(() => setOutlets([]))
      .finally(() => setLoading(false));
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedOutlet = outlets.find((o) => o.id === selected[0]) ?? null;

  const filtered = outlets
    .filter(
      (o) =>
        o.name.toLowerCase().includes(query.toLowerCase()) ||
        (o.city ?? '').toLowerCase().includes(query.toLowerCase()),
    )
    .slice(0, 10);

  function handleSelect(id: string) {
    onChange([id]);
    setOpen(false);
    setQuery('');
  }

  function handleClear() {
    onChange([]);
    setQuery('');
  }

  function handleInputChange(value: string) {
    setQuery(value);
    if (!open) setOpen(true);
  }

  async function addOutlet() {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const outlet = await api.post<OutletItem>('/claims/outlets', { name: newName.trim() });
      setOutlets((prev) => [...prev, outlet]);
      onChange([outlet.id]);
      setNewName('');
      setOpen(false);
    } catch {
      // swallow — API doesn't support ad-hoc outlet creation yet; show existing list
    } finally {
      setAdding(false);
    }
  }

  if (loading) return <p className="text-sm text-slate-400">{t('common.loading')}</p>;

  return (
    <div>
      <label className="block text-xs font-semibold tracking-wide uppercase text-slate-500 mb-1.5">
        {t('wizard.labels.outletHelpText')}
      </label>

      <div ref={containerRef} className="relative">
        {/* Combobox input */}
        <div
          className={[
            'flex items-center border rounded bg-bone-soft transition-colors',
            open ? 'border-copper' : 'border-hairline',
          ].join(' ')}
        >
          {selectedOutlet ? (
            <>
              <span className="flex-1 px-3 py-2 text-sm text-ink truncate">{selectedOutlet.name}</span>
              <button
                type="button"
                onClick={handleClear}
                className="px-3 py-2 text-slate-400 hover:text-copper transition-colors text-sm font-medium"
                aria-label="Clear selection"
              >
                ×
              </button>
            </>
          ) : (
            <input
              type="text"
              value={query}
              onChange={(e) => handleInputChange(e.target.value)}
              onFocus={() => setOpen(true)}
              placeholder={t('wizard.labels.searchOutlets')}
              className="flex-1 px-3 py-2 text-sm bg-transparent focus:outline-none"
            />
          )}
        </div>

        {/* Dropdown list */}
        {open && !selectedOutlet && (
          <div className="absolute z-10 top-full left-0 right-0 mt-1 border border-hairline rounded bg-bone-soft shadow-lg max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-slate-400 italic">
                {outlets.length === 0
                  ? t('wizard.labels.noOutletsFound')
                  : t('wizard.labels.noOutletsMatch')}
              </p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => handleSelect(o.id)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-copper-soft transition-colors flex items-center gap-2"
                >
                  <span className="font-medium text-ink">{o.name}</span>
                  {o.city && <span className="text-slate-400 text-xs">— {o.city}</span>}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Add new outlet */}
      <div className="flex gap-2 mt-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder={t('wizard.labels.addOutletPlaceholder')}
          className="flex-1 border border-hairline rounded px-3 py-1.5 text-sm bg-bone-soft focus:outline-none focus:border-copper"
          onKeyDown={(e) => e.key === 'Enter' && void addOutlet()}
        />
        <button
          type="button"
          onClick={() => void addOutlet()}
          disabled={adding || !newName.trim()}
          className="px-3 py-1.5 text-sm bg-ink text-bone-soft rounded hover:bg-ink-soft disabled:opacity-40 transition-colors"
        >
          {t('wizard.actions.addOutlet')}
        </button>
      </div>
    </div>
  );
}
